import React, { useState, useMemo } from "react";
import { Star } from "lucide-react";
import type { DataRecord, SocialQueuePayload, SocialQueueItem, SocialOverviewPayload } from "../../types";
import { getClientBrandMeta } from "./socialBrand";
import { SocialCommentDetailModal } from "./SocialCommentDetailModal";
import { SocialVoiceSection } from "./sections/SocialVoiceSection";
import { SocialActionImmediateSection } from "./sections/SocialActionImmediateSection";
import { SocialTrendSection } from "./sections/SocialTrendSection";
import { SocialDiagnosisSection } from "./sections/SocialDiagnosisSection";
import { SocialOpportunitySection } from "./sections/SocialOpportunitySection";
import { SocialActionPlanSection } from "./sections/SocialActionPlanSection";

interface Props {
  clientSlug: string;
  activePlatform: string;
  comments: DataRecord[];
  allComments?: DataRecord[];
  queue: SocialQueuePayload | null;
  overview: SocialOverviewPayload | null;
  onOpenReplyModal: (item: SocialQueueItem) => void;
}

export function SocialExecutiveSections({
  clientSlug,
  activePlatform,
  comments,
  allComments,
  queue,
  overview,
  onOpenReplyModal,
}: Props) {
  const brandMeta = getClientBrandMeta(clientSlug);
  const [selectedCommentForModal, setSelectedCommentForModal] = useState<DataRecord | SocialQueueItem | null>(null);

  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);
  const number = (v: any, fallback = 0) => {
    const n = Number(v);
    return isNaN(n) ? fallback : n;
  };

  const windowDays = overview?.window_days ?? 28;

  // Detección y banderas de plataforma activa
  const isMetaIg = activePlatform === "meta_ig" || activePlatform === "instagram";
  const isMetaFb = activePlatform === "meta_fb" || activePlatform === "facebook";
  const isGoogle = activePlatform === "gbp" || activePlatform === "google";
  const isGlobal = activePlatform === "all" || !activePlatform;

  // Comentarios pertenecientes a la plataforma activa
  const platformComments = useMemo(() => {
    if (isGlobal) return comments;
    return comments.filter((r) => {
      const p = text(r.platform).toLowerCase();
      if (isMetaIg) return p === "meta_ig" || p.includes("ig") || p.includes("instagram");
      if (isMetaFb) return p === "meta_fb" || p.includes("fb") || p.includes("facebook");
      if (isGoogle) return p === "gbp" || p.includes("google");
      return false;
    });
  }, [comments, isGlobal, isMetaIg, isMetaFb, isGoogle]);

  const platformColor = isMetaIg
    ? "#e1306c"
    : isMetaFb
    ? "#1877f2"
    : isGoogle
    ? "#34a853" // Verde Google oficial
    : "#7d96ff";

  const platformBadgeLabel = isMetaIg
    ? "Instagram"
    : isMetaFb
    ? "Facebook"
    : isGoogle
    ? "Google BP"
    : "Visión Global";

  const renderPlatformBadge = () => (
    <span
      className="social-role-badge"
      style={{
        background: isMetaIg
          ? "rgba(225, 48, 108, 0.15)"
          : isMetaFb
          ? "rgba(24, 119, 242, 0.15)"
          : isGoogle
          ? "rgba(52, 168, 83, 0.15)"
          : "rgba(125, 150, 255, 0.15)",
        color: platformColor,
        fontWeight: 700,
        borderColor: "transparent",
      }}
    >
      {platformBadgeLabel}
    </span>
  );

  const hasGoogleData = useMemo(() => {
    return comments.some(
      (c) => text(c.platform).toLowerCase() === "gbp" && c.star_rating !== null && c.star_rating !== undefined
    );
  }, [comments]);

  const showGoogleRatings = isGoogle || (isGlobal && hasGoogleData);

  // Casos críticos para la sección 03 (Acción inmediata) filtrados por plataforma
  const criticalItems: SocialQueueItem[] = useMemo(() => {
    let rawItems: SocialQueueItem[] = [];
    if (queue?.items && queue.items.length > 0) {
      rawItems = queue.items;
    } else {
      const negs = comments.filter((c) => text(c.polaridad).toLowerCase() === "negativa" && !c.has_reply);
      rawItems = negs.slice(0, 4).map((c) => ({
        feedback_id: text(c.feedback_id),
        date: text(c.date),
        created_time: text(c.date),
        platform: text(c.platform, "gbp"),
        origin: text(c.origin, "organic"),
        author_name: text(c.author_name, "Anónimo"),
        message: text(c.message),
        star_rating: c.star_rating !== null && c.star_rating !== undefined ? number(c.star_rating) : null,
        has_reply: false,
        post_id: text(c.post_id),
        ad_account_id: null,
        page_id: text(c.page_id),
        action_link: null,
        polaridad: "negativa",
        acto_de_habla: text(c.acto_de_habla, "reclamo"),
        aspecto: text(c.aspecto, "servicio"),
        plan: {
          action: "escalate",
          priority: 1,
          reason: "Comentario o reseña negativa pendiente",
          template_key: "standard",
          draft_text: `Hola ${text(c.author_name, "estimado/a")}. Lamentamos sinceramente que tu experiencia no haya sido la esperada. Queremos revisarlo con el equipo para darte una solución pronta. ¿Podrías escribirnos por mensaje privado para coordinar directamente?`,
        },
      }));
    }

    if (activePlatform !== "all") {
      return rawItems.filter((i) => text(i.platform).toLowerCase() === activePlatform.toLowerCase());
    }
    return rawItems;
  }, [queue, comments, activePlatform]);

  // Distribución de estrellas (Sección 04)
  const starCounts = useMemo(() => {
    const counts: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    for (const c of comments) {
      if (c.star_rating !== null && c.star_rating !== undefined) {
        const s = Math.min(5, Math.max(1, Math.round(number(c.star_rating))));
        counts[s] = (counts[s] || 0) + 1;
      }
    }
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return { counts, total: Math.max(1, total) };
  }, [comments]);

  // Aspectos negativos dinámicos según comentarios de la plataforma activa
  const negativeAspectList = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of platformComments) {
      if (text(c.polaridad).toLowerCase() === "negativa" && c.aspecto) {
        const asp = text(c.aspecto).trim().toLowerCase();
        if (asp) counts[asp] = (counts[asp] || 0) + 1;
      }
    }
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return [];
    const maxVal = entries[0][1] || 1;
    return entries.slice(0, 6).map(([key, count]) => ({
      label: key === "servicio" ? "Atención y servicio" : key === "tiempos" ? "Tiempos de respuesta" : key,
      count,
      pct: Math.round((count / maxVal) * 100),
    }));
  }, [platformComments]);

  // Aspectos positivos dinámicos según comentarios de la plataforma activa
  const positiveAspectList = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of platformComments) {
      if (text(c.polaridad).toLowerCase() === "positiva" && c.aspecto) {
        const asp = text(c.aspecto).trim().toLowerCase();
        if (asp) counts[asp] = (counts[asp] || 0) + 1;
      }
    }
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return [];
    const maxVal = entries[0][1] || 1;
    return entries.slice(0, 6).map(([key, count]) => ({
      label: key === "atención" || key === "servicio" ? "Excelente atención" : key === "calidad" ? "Calidad y profesionalismo" : key,
      count,
      pct: Math.round((count / maxVal) * 100),
    }));
  }, [platformComments]);

  // Testimonios positivos de prueba social
  const positiveTestimonials = useMemo(() => {
    return platformComments
      .filter((c) => text(c.polaridad).toLowerCase() === "positiva" && text(c.message).length > 20)
      .slice(0, 4);
  }, [platformComments]);

  const renderStars = (rating: number, color = "#f59e0b") => (
    <span style={{ display: "inline-flex", gap: "2px", alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={12}
          fill={s <= rating ? color : "transparent"}
          color={s <= rating ? color : "var(--dim, #5e6677)"}
        />
      ))}
    </span>
  );

  function isCdnMediaUrl(url: string): boolean {
    if (!url) return false;
    const lower = url.toLowerCase();
    return (
      lower.includes("scontent") ||
      lower.includes("cdninstagram.com") ||
      lower.includes("fbcdn.net") ||
      lower.includes(".jpg?") ||
      lower.includes(".jpeg?") ||
      lower.includes(".png?") ||
      lower.includes(".webp?") ||
      lower.includes(".mp4?")
    );
  }

  const getDirectLink = (row: DataRecord | SocialQueueItem) => {
    const defaultPlat = isGoogle ? "gbp" : isMetaIg ? "meta_ig" : isMetaFb ? "meta_fb" : "";
    const pl = text(row.platform, defaultPlat).toLowerCase();
    const permalink = text((row as any).post_permalink);

    if (pl === "gbp" || pl.includes("google")) {
      const mediaUrl = text((row as any).post_media_url);
      if (mediaUrl && mediaUrl.startsWith("https://maps.google.com")) return mediaUrl;
      if (permalink && permalink.startsWith("https://maps.google.com")) return permalink;
      const query = `${brandMeta.displayName} opiniones`;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    }

    if (pl === "meta_ig" || pl.includes("ig") || pl.includes("instagram")) {
      if (permalink && permalink.startsWith("http") && !isCdnMediaUrl(permalink) && permalink.includes("instagram.com")) {
        return permalink;
      }
      const handle = brandMeta.instagramHandle || clientSlug;
      return `https://www.instagram.com/${handle}/`;
    }

    if (pl === "meta_fb" || pl.includes("fb") || pl.includes("facebook")) {
      if (permalink && permalink.startsWith("http") && !isCdnMediaUrl(permalink) && permalink.includes("facebook.com")) {
        return permalink;
      }
      const postId = text((row as any).post_id);
      const [pageId, postSuffix] = postId.split("_");
      if (pageId && postSuffix) {
        return `https://www.facebook.com/${pageId}/posts/${postSuffix}`;
      }
      return "https://www.facebook.com/";
    }

    if (permalink && permalink.startsWith("http") && !isCdnMediaUrl(permalink)) return permalink;
    return "#";
  };

  if (!isGlobal && platformComments.length === 0) {
    return (
      <div className="social-editorial-container" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <section
          className="social-editorial-section"
          style={{
            textAlign: "center",
            padding: "48px 24px",
            background: "var(--surface, #11141b)",
            borderRadius: "14px",
            border: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
          }}
        >
          <div className="social-section-tag">{platformBadgeLabel}</div>
          <h2 className="social-section-title" style={{ fontSize: "20px", margin: "10px 0 6px 0" }}>
            Sin actividad registrada en {platformBadgeLabel}
          </h2>
          <p className="social-section-subtitle" style={{ margin: 0 }}>
            {brandMeta.displayName} no registra publicaciones ni comentarios en {platformBadgeLabel} durante el período analizado.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="social-editorial-container" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* Sección 02: Voz de la comunidad / paciente */}
      <SocialVoiceSection
        clientSlug={clientSlug}
        activePlatform={activePlatform}
        comments={platformComments}
        windowDays={windowDays}
        onOpenReplyModal={onOpenReplyModal}
        onSelectCommentForModal={(item) => setSelectedCommentForModal(item)}
        getDirectLink={getDirectLink}
        renderStars={renderStars}
        renderPlatformBadge={renderPlatformBadge}
      />

      {/* Sección 03: Acción inmediata (casos críticos con borrador solo en modal) */}
      <SocialActionImmediateSection
        clientSlug={clientSlug}
        activePlatform={activePlatform}
        criticalItems={criticalItems}
        onOpenReplyModal={onOpenReplyModal}
        onSelectCommentForModal={(item) => setSelectedCommentForModal(item)}
        getDirectLink={getDirectLink}
        renderStars={renderStars}
        renderPlatformBadge={renderPlatformBadge}
      />

      {/* Sección 04: Tendencia y distribución de notas */}
      <SocialTrendSection
        clientSlug={clientSlug}
        activePlatform={activePlatform}
        comments={comments}
        allComments={allComments}
        overview={overview}
        windowDays={windowDays}
        starCounts={starCounts}
      />

      {/* Sección 05: Diagnóstico de fricciones y aspectos */}
      <SocialDiagnosisSection
        activePlatform={activePlatform}
        showGoogleRatings={showGoogleRatings}
        windowDays={windowDays}
        negativeAspectList={negativeAspectList}
        renderPlatformBadge={renderPlatformBadge}
      />

      {/* Sección 06: Oportunidad y prueba social */}
      <SocialOpportunitySection
        activePlatform={activePlatform}
        showGoogleRatings={showGoogleRatings}
        windowDays={windowDays}
        positiveAspectList={positiveAspectList}
        positiveTestimonials={positiveTestimonials}
        renderStars={renderStars}
        renderPlatformBadge={renderPlatformBadge}
        getDirectLink={getDirectLink}
        onSelectCommentForModal={(item) => setSelectedCommentForModal(item)}
      />

      {/* Sección 07: Plan de acción y propuesta de valor por área */}
      <SocialActionPlanSection
        activePlatform={activePlatform}
        showGoogleRatings={showGoogleRatings}
        criticalCount={criticalItems.length}
        clientSlug={clientSlug}
      />

      {/* Modal para ver comentario completo si el texto es largo */}
      <SocialCommentDetailModal
        isOpen={Boolean(selectedCommentForModal)}
        onClose={() => setSelectedCommentForModal(null)}
        item={selectedCommentForModal}
        clientSlug={clientSlug}
        onOpenReplyModal={onOpenReplyModal}
        getDirectLink={getDirectLink}
      />
    </div>
  );
}
